import Tooltip from "../../components/common/Tooltip";
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { db, timestamp } from "../../firebase/config";
import { useState, useContext, useEffect } from "react";
import firebase from '../../firebase/compat'
import { SavedIcon } from "../../StateManagment/SavedIcon";
import { Auth } from "../../StateManagment/Auth";
import DeleteModal from "../../components/common/DeleteModal";
import { useHistory } from "react-router-dom";
import { client } from "../../hooks/Client";
import { useFirestoreNoCompagnyGeneralArrayContains, useFirestoreGeneralArrayContains } from "../../firebase/useFirestore";
import notDeleted from "../../helpers/notDeleted";
import resolveIndicatorDeleteAction from "../../helpers/effects/resolveIndicatorDeleteAction";
import { isDeletedStatus } from "../synchronisations/syncDuplicateCheck";

const EffectDeleteContainer = ({effect}) => {
    // Context
    const [saved, setSaved] = useContext(SavedIcon)
    const [auth] = useContext(Auth)

    // State
    const [paired, setPaired] = useState(false)
    const [deleteModal, setDeleteModal] = useState(false)
    const [deleteModalSync, setDeleteModalSync] = useState(false)
    const [deleteDocid, setDeleteDocid] = useState('')
    const [deleteName, setDeleteName] = useState('')
    const [deleteId, setDeleteId] = useState('')

    // Hooks
    const history = useHistory()

    // Firestore
    const syncs = useFirestoreNoCompagnyGeneralArrayContains('Synchronisations', 'ProjectEffect', effect.ID || '', 'Members', client)

    // Live indicators linked to this effect (soft-deleted ones are filtered out
    // centrally by the hook). Drives the optional cascade-delete checkbox.
    // Only subscribes while the plain delete modal is open — an empty id makes
    // the query engine skip the listener, so no per-row subscription on the list.
    const linkedIndicators = useFirestoreGeneralArrayContains('QuestionnaireFields', 'EffectId', deleteModal ? (effect.ID || '') : '')

    // Cascade choice offered only on the plain (unpaired) delete modal.
    const linkedOptions = [
        {
            key: 'indicators',
            label: 'Verwijder ook de gekoppelde indicatoren',
            count: linkedIndicators?.length || 0,
        },
    ]

    // Sync ID
    const syncID = syncs && syncs[0]?.ID || ''

    // Delete modal 
    const deleteModalHandler = (e) => {
        const docid = e.currentTarget.dataset.docid
        const deleteName = e.currentTarget.dataset.deletename
        const id = e.currentTarget.dataset.id

        setDeleteDocid(docid)
        setDeleteName(deleteName)
        setDeleteId(id)

        setDeleteModal(!deleteModal)
    }

    // Delete modal sync handler
    const deleteModalsyncHandler = (e) => {
        const docid = e.currentTarget.dataset.docid
        const deleteName = e.currentTarget.dataset.deletename
        const id = e.currentTarget.dataset.id

        setDeleteDocid(docid)
        setDeleteName(deleteName)
        setDeleteId(id)

        setDeleteModalSync(!deleteModalSync)
    }

    // Soft-delete the effect (ADR-008), then cascade to its indicators.
    // `selectedKeys` comes from the DeleteModal checkboxes; the synced modal
    // calls without arguments, so shared/synced effects never delete indicators.
    const deleteEffect = async (selectedKeys) => {
        const deleteLinkedIndicators = Array.isArray(selectedKeys) && selectedKeys.includes('indicators')

        await db.collection('OutputEffects')
        .doc(deleteDocid)
        .update({
            Deleted: true,
            DeletedAt: timestamp,
            DeletedBy: auth?.ID || '',
        })

        await cascadeIndicators(deleteId, deleteLinkedIndicators)

        setSaved('flex')
    }

     // For each indicator linked to this effect: soft-delete it when it is solely
     // linked here and the user opted in, otherwise just unlink this effect from it.
     // Reads directly (includes soft-deleted docs) so the helper can skip them.
     const cascadeIndicators = async (id, deleteLinkedIndicators) => {
        const querySnapshot = await db.collection('QuestionnaireFields')
        .where('EffectId', 'array-contains', id)
        .get()

        await Promise.all(querySnapshot.docs.map((doc) => {
            const action = resolveIndicatorDeleteAction(doc.data(), id, deleteLinkedIndicators)

            if (action === 'soft-delete') {
                return doc.ref.update({
                    Deleted: true,
                    DeletedAt: timestamp,
                    DeletedBy: auth?.ID || '',
                })
            }

            if (action === 'unlink') {
                return doc.ref.update({
                    EffectId: firebase.firestore.FieldValue.arrayRemove(id)
                })
            }

            return null
        }))
    }

    // Check if effect is paired with a portfolio account
    const effectIsPaired = async (id) => {
            
        const querySnapshot = await db.collection('Synchronisations')
        .where('ProjectEffect', '==', id)
        // Tenant-scope + secure against the Members-based Synchronisations rule.
        // AI-CONTEXT: See docs/pitfalls.md#security-synchronisations-members-tenant-gate
        .where('Members', 'array-contains', client)
        .get()


        // Only active pairings count — exclude soft-deleted syncs (ADR-008) and
        // rows whose Status marks them deleted.
        const activePairings = querySnapshot.docs.filter((d) => {
            const data = d.data()
            return notDeleted(data) && !isDeletedStatus(data.Status)
        })
        activePairings.length > 0 ? setPaired(true) : setPaired(false)
    }

    useEffect(() => {
        effectIsPaired(effect.ID)
    }, [effect])

     // Update sync
        const updateSync = () => {
            db.collection('Synchronisations')
            .where('ID', '==', syncID)
            // Tenant-scope + secure against the Members-based Synchronisations rule.
            // AI-CONTEXT: See docs/pitfalls.md#security-synchronisations-members-tenant-gate
            .where('Members', 'array-contains', client)
            .get()
            .then((querySnapshot) => {
                querySnapshot.forEach((doc) => {
                    db.collection('Synchronisations')
                    .doc(doc.id)
                    .update({
                        Status: 'deleted-project'
                    })
                })
            })
        }


    // Met een actieve synchronisatie: geen vinkjes in dit venster, dus de
    // indicatoren worden alleen losgekoppeld ([] = niets aangevinkt), en de
    // koppeling met het portfolio wordt gemarkeerd.
    // AI-CONTEXT: See docs/pitfalls.md#ui-soft-delete-promises-a-trash-it-is-not-in
    const confirmSyncedDelete = () => {
        deleteEffect([])
        updateSync()
    }

  return (
    <div className="table-delete-container">

        {/* Show this tooltip message if effect is standardised and not paired */}
        {effect.Standard === 'true' && paired === false ? (
            <Tooltip content={'Let op: dit is een gestandaardiseerd effect'} top='-80px' left='-50px'>
               <DeleteOutlinedIcon className='table-delete-icon' data-docid={effect.docid} data-deletename={effect.Effect} data-id={effect.ID} onClick={deleteModalHandler} />
            </Tooltip>
        ) : null}
    
        {/* Show this tooltip message only if effect is paired AND NOT an MSI effect */}
        {effect.Standard === undefined && paired === true ? (
            <Tooltip content={'Let op: dit effect is gekoppeld aan een portfolio account'} top='-100px' left='-50px'>
                <DeleteOutlinedIcon className='table-delete-icon' data-docid={effect.docid} data-deletename={effect.Effect} data-id={effect.ID} onClick={deleteModalsyncHandler}/>
            </Tooltip>
        ) : null}

        {/* Show this tooltip message only if effect is paired AND NOT an MSI effect */}
        {effect.Standard === 'false' && paired === true ? (
            <Tooltip content={'Let op: dit effect is gekoppeld aan een portfolio account'} top='-100px' left='-50px'>
                <DeleteOutlinedIcon className='table-delete-icon' data-docid={effect.docid} data-deletename={effect.Effect} data-id={effect.ID} onClick={deleteModalsyncHandler}/>
            </Tooltip>
        ) : null}

        {/* Show this tooltip message only if effect is paired AND a MSI effect */}
        {effect.Standard === 'true' && paired === true ? (
            <Tooltip content={'Let op: Dit gestandaardiseerde effect is gekoppeld aan een portfolio account'} top='-100px' left='-50px'>
                <DeleteOutlinedIcon className='table-delete-icon' data-docid={effect.docid} data-deletename={effect.Effect} data-id={effect.ID} onClick={deleteModalsyncHandler}/>
            </Tooltip>
        ) : null}
    
        {/* Show this tooltip message only if effect is NOT paired and NOT standardised */}
        {effect.Standard === undefined  && paired === false ? (
            <Tooltip content={'Effect verwijderen'} top='-80px' left='-50px'>
                <DeleteOutlinedIcon className='table-delete-icon' data-docid={effect.docid} data-deletename={effect.Effect} data-id={effect.ID} onClick={deleteModalHandler} />
            </Tooltip>
        ) : null}

        {/* Show this tooltip message only if effect is NOT paired and NOT standardised */}
        {effect.Standard === 'false'  && paired === false ? (
            <Tooltip content={'Effect verwijderen'} top='-80px' left='-50px'>
                <DeleteOutlinedIcon className='table-delete-icon' data-docid={effect.docid} data-deletename={effect.Effect} data-id={effect.ID} onClick={deleteModalHandler} />
            </Tooltip>
        ) : null}

        <DeleteModal deleteModal={deleteModal} setDeleteModal={setDeleteModal} deleteItem={deleteEffect} deleteName={deleteName} linkedOptions={linkedOptions}
  softDelete
/>
         <DeleteModal deleteModal={deleteModalSync} setDeleteModal={setDeleteModalSync} deleteItem={confirmSyncedDelete} deleteName={deleteName}
  softDelete
  activeSync
  linkedNote="De gekoppelde indicatoren blijven bestaan; ze worden alleen losgekoppeld van dit effect."
/>
    </div>
  )
}

export default EffectDeleteContainer